import React, { useState, useEffect, useRef } from "react";
import { AiFillGithub } from "react-icons/ai";
import { BsCalendar3 } from "react-icons/bs";
import {
  MdLocalFireDepartment, MdBolt, MdCalendarMonth, MdKey, MdWarningAmber,
} from "react-icons/md";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

/* Returns indigo color for a contribution intensity 0-1 */
function cellColor(ratio) {
  if (ratio <= 0)    return null; // empty — CSS handles
  if (ratio < 0.2)   return { bg: "rgba(99,102,241,0.20)", glow: false };
  if (ratio < 0.45)  return { bg: "rgba(99,102,241,0.45)", glow: false };
  if (ratio < 0.70)  return { bg: "rgba(99,102,241,0.72)", glow: false };
  return               { bg: "rgb(99,102,241)",             glow: true  };
}

/* Which month label to show above each week column */
function getMonthLabels(weeks) {
  const labels = [];
  let lastMonth = -1;
  weeks.forEach((week, wi) => {
    const firstDay = week[0];
    if (!firstDay) { labels.push(null); return; }
    const m = new Date(firstDay.date).getMonth();
    if (m !== lastMonth) { labels.push(MONTHS[m]); lastMonth = m; }
    else labels.push(null);
  });
  return labels;
}

/* ── Tooltip component ── */
function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  const [pos,  setPos]  = useState({ x: 0, y: 0 });
  const ref = useRef(null);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ x: e.clientX - r.left, y: e.clientY - r.top });
  };

  return (
    <div
      ref={ref}
      className="hm-cell-wrap"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onMouseMove={onMove}
    >
      {children}
      {show && (
        <div
          className="hm-tooltip"
          style={{ left: pos.x + 10, top: pos.y - 34 }}
        >
          {text}
        </div>
      )}
    </div>
  );
}

function GithubHeatmap() {
  const USERNAME = "MuhammadMuaaz007";
  const YEAR     = new Date().getFullYear();
  const token    = process.env.REACT_APP_GITHUB_TOKEN;

  const [loading, setLoading]   = useState(true);
  const [error,   setError]     = useState(null);
  const [weeks,   setWeeks]     = useState([]);
  const [total,   setTotal]     = useState(0);

  useEffect(() => {
    async function fetchData() {
      setLoading(true); setError(null);
      if (!token) {
        setError("no-token");
        setLoading(false); return;
      }
      const from  = `${YEAR}-01-01T00:00:00Z`;
      const to    = `${YEAR}-12-31T23:59:59Z`;
      const query = `query($login:String!,$from:DateTime!,$to:DateTime!){
        user(login:$login){
          contributionsCollection(from:$from,to:$to){
            contributionCalendar{
              totalContributions
              weeks{ contributionDays{ date contributionCount } }
            }
          }
        }
      }`;
      try {
        const res  = await fetch("https://api.github.com/graphql", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `bearer ${token}`,
          },
          body: JSON.stringify({ query, variables: { login: USERNAME, from, to } }),
        });
        if (!res.ok) throw new Error(`GitHub API error ${res.status}`);
        const json = await res.json();
        if (json.errors) throw new Error(json.errors.map((e) => e.message).join("; "));
        const cal  = json.data.user.contributionsCollection.contributionCalendar;
        setWeeks(cal.weeks.map((w) => w.contributionDays));
        setTotal(cal.totalContributions);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [token, YEAR]);

  const allCounts   = weeks.flat().map((d) => d.contributionCount || 0);
  const maxCount    = allCounts.length ? Math.max(...allCounts) : 0;
  const monthLabels = getMonthLabels(weeks);

  /* Streak calculation */
  const flat = weeks.flat();
  let streak = 0, best = 0, cur = 0;
  flat.forEach((d) => {
    if (d.contributionCount > 0) { cur++; if (cur > best) best = cur; }
    else cur = 0;
  });
  // current streak (from end going backwards)
  for (let i = flat.length - 1; i >= 0; i--) {
    if (flat[i].contributionCount > 0) streak++;
    else break;
  }

  const activeDays = flat.filter((d) => d.contributionCount > 0).length;

  return (
    <section className="heatmap-section">
      <div className="heatmap-container">

        {/* ── Header ── */}
        <div className="hm-header">
          <div>
            <span className="section-label">Activity</span>
            <h2 className="section-title" style={{ marginBottom: 4 }}>
              Days I <span>Code</span>
            </h2>
            <p className="hm-subtitle">
              GitHub contribution graph for{" "}
              <a
                href={`https://github.com/${USERNAME}`}
                target="_blank"
                rel="noreferrer"
                className="about-inline-link"
              >
                @{USERNAME}
              </a>{" "}
              in {YEAR}
            </p>
          </div>
          <a
            href={`https://github.com/${USERNAME}`}
            target="_blank"
            rel="noreferrer"
            className="hm-gh-btn"
          >
            <AiFillGithub />
            View Profile
          </a>
        </div>

        {/* ── Stats pills (only when data loaded) ── */}
        {!loading && !error && total > 0 && (
          <div className="hm-stats-row">
            <div className="hm-stat">
              <BsCalendar3 className="hm-stat-icon" />
              <div className="hm-stat-text">
                <div className="hm-stat-num">{total.toLocaleString()}</div>
                <div className="hm-stat-lbl">Total Contributions</div>
              </div>
            </div>
            <div className="hm-stat">
              <MdLocalFireDepartment className="hm-stat-emoji" style={{ color: "#f97316" }} />
              <div className="hm-stat-text">
                <div className="hm-stat-num">{streak}</div>
                <div className="hm-stat-lbl">Current Streak</div>
              </div>
            </div>
            <div className="hm-stat">
              <MdBolt className="hm-stat-emoji" style={{ color: "#f59e0b" }} />
              <div className="hm-stat-text">
                <div className="hm-stat-num">{best}</div>
                <div className="hm-stat-lbl">Best Streak</div>
              </div>
            </div>
            <div className="hm-stat">
              <MdCalendarMonth className="hm-stat-emoji" style={{ color: "#6366f1" }} />
              <div className="hm-stat-text">
                <div className="hm-stat-num">{activeDays}</div>
                <div className="hm-stat-lbl">Active Days</div>
              </div>
            </div>
          </div>
        )}

        {/* ── Main card ── */}
        <div className="hm-card">

          {loading && (
            <div className="hm-loading">
              <div className="hm-spinner" />
              <span>Fetching contribution data…</span>
            </div>
          )}

          {!loading && error === "no-token" && (
            <div className="hm-no-token">
              <MdKey className="hm-no-token-icon" />
              <p className="hm-no-token-title">GitHub Token Required</p>
              <p className="hm-no-token-sub">
                Add <code>REACT_APP_GITHUB_TOKEN</code> to your <code>.env</code> file
                to display the live contribution graph.
              </p>
            </div>
          )}

          {!loading && error && error !== "no-token" && (
            <div className="hm-error">
              <MdWarningAmber style={{ fontSize: "1.2rem" }} /> {error}
            </div>
          )}

          {!loading && !error && (
            <div className="hm-graph-wrap">
              {/* Day-of-week labels */}
              <div className="hm-day-labels">
                {DAYS.map((d, i) => (
                  <div key={d} className="hm-day-label">
                    {i % 2 === 1 ? d : ""}
                  </div>
                ))}
              </div>

              <div className="hm-right-side">
                {/* Month labels */}
                <div className="hm-month-row">
                  {monthLabels.map((label, i) => (
                    <div key={i} className="hm-month-cell">
                      {label && <span className="hm-month-label">{label}</span>}
                    </div>
                  ))}
                </div>

                {/* Grid */}
                <div className="hm-grid">
                  {weeks.map((week, wi) => (
                    <div key={wi} className="hm-week">
                      {week.map((day) => {
                        const ratio = maxCount > 0 ? day.contributionCount / maxCount : 0;
                        const col   = cellColor(ratio);
                        const label = `${day.date}: ${day.contributionCount} contribution${day.contributionCount !== 1 ? "s" : ""}`;
                        return (
                          <Tooltip key={day.date} text={label}>
                            <div
                              className={`hm-cell${col?.glow ? " hm-cell-glow" : ""}`}
                              style={col ? { background: col.bg } : undefined}
                            />
                          </Tooltip>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Legend */}
          {!loading && !error && (
            <div className="hm-legend">
              <span className="hm-legend-lbl">Less</span>
              {[0, 0.2, 0.45, 0.70, 1].map((r, i) => {
                const col = cellColor(r);
                return (
                  <div
                    key={i}
                    className="hm-cell"
                    style={col ? { background: col.bg } : undefined}
                  />
                );
              })}
              <span className="hm-legend-lbl">More</span>
            </div>
          )}
        </div>

      </div>
    </section>
  );
}

export default GithubHeatmap;
